import { Link } from "react-router-dom";
import {
  ArrowRight,
  PlayCircle,
  CheckCircle2,
  Sparkles,
  Brain,
  ShieldCheck,
  Search,
  Target,
  FileCheck2,
  Send,
  BarChart3,
  Wand2,
} from "lucide-react";
import { HomeResumeMatchTester } from "@/components/HomeResumeMatchTester";

/**
 * TailorWizMarketingHome — public-facing landing page.
 * Copy is Marcus Hall's voice from TailorWiz_Website_Copy.docx.
 * Brand: navy primary + gold (#C9A84C) accent CTAs. Executive feel.
 */
export function TailorWizMarketingHome() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <MarketingNav />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        {/* Subtle ambient gold glow */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.18]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 30%, #C9A84C 0, transparent 38%), radial-gradient(circle at 82% 70%, #4A78C2 0, transparent 38%)",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-14 pb-12 sm:pt-20 sm:pb-16 text-center">
          {/* Eyebrow */}
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] text-[#C9A84C] bg-white/5 px-4 py-2 rounded-full border border-[#C9A84C]/40 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            For job seekers at every level — entry to executive — who are done being ignored
          </span>

          {/* Headline */}
          <h1 className="mt-6 text-[34px] sm:text-[48px] lg:text-[58px] font-extrabold tracking-tight leading-[1.05] text-white">
            Tailor Your Resume to
            <br className="hidden sm:inline" />{" "}
            <span className="text-[#C9A84C]">ANY Job</span> in{" "}
            <span className="text-white underline decoration-[#C9A84C] decoration-[4px] underline-offset-[6px]">
              60 Seconds
            </span>
            .
            <span className="block mt-3 text-[17px] sm:text-[22px] lg:text-[26px] font-semibold italic text-[#F5E7BA] tracking-tight">
              90% Match Score, 100% ATS Pass Rate!
            </span>
          </h1>

          {/* Subheadline */}
          <p className="mt-6 text-[16px] sm:text-[19px] leading-[1.6] text-slate-200 max-w-3xl mx-auto">
            Then in just a few clicks, find the decision maker and land your
            tailored resume directly in their inbox.{" "}
            <span className="font-semibold text-white">
              No ATS. No recruiters. No HR gatekeepers.
            </span>
          </p>

          {/* Before/After score card — the product proof */}
          <div id="hero-video" className="mt-10 max-w-3xl mx-auto">
            <HeroScoreCard />
          </div>

          {/* Bullets — 6 in a centered 2-column grid, inside a boxed card */}
          <div className="mt-10 max-w-3xl mx-auto rounded-2xl border border-[#C9A84C]/30 bg-white/[0.04] backdrop-blur-sm px-6 py-6 sm:px-8 sm:py-7 shadow-lg shadow-black/20">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-left">
              {[
                "Mimics World Renowned Executive Resume Writer Marcus Hall's Writing Style",
                "Pass the 2026 ATS Algorithm 100% of the Time!",
                "Tailors your resume, cover letter, and LinkedIn profile in one run",
                "Convince the Humans that You are the Answer to their problems",
                "100% Honest, Accurate & Built around the Company's Real Pain Points",
                "Locates the Hiring Manager's contact info for all roles for direct delivery",
              ].map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2.5 text-[14px] sm:text-[15px] leading-[1.45] text-slate-100"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold tracking-tight shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] hover:shadow-xl transition-all"
            >
              Try It Out FREE Now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#hero-video"
              className="inline-flex items-center gap-2 text-[15px] font-semibold text-slate-200 hover:text-[#C9A84C] transition-colors"
            >
              <PlayCircle className="w-5 h-5" />
              Watch the 90-second demo
            </a>
          </div>
          <p className="mt-4 text-[12px] text-slate-300 font-medium">
            No credit card required.{" "}
            <span className="mx-2 text-slate-500">•</span>
            Cancel anytime.{" "}
            <span className="mx-2 text-slate-500">•</span>
            90% match score guaranteed every run.
          </p>
        </div>

        {/* Stat bar */}
        <div className="relative max-w-6xl mx-auto px-6 pb-16 sm:pb-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/15 backdrop-blur-sm">
            <DarkHeroStat value="60 seconds" label="From job posting to fully tailored package" />
            <DarkHeroStat value="90%+ match" label="On every tailored document, every run" />
            <DarkHeroStat value="6,000+ sources" label="Scanned per company through JobIntel 360" />
            <DarkHeroStat value="85% interview rate" label="With the Solution Asset" />
          </div>
        </div>
      </section>

      {/* ============ FREE RESUME MATCH TESTER ============ */}
      <HomeResumeMatchTester />

      {/* ============ PROBLEM SECTION ============ */}
      <section className="bg-[#0B1C3F] text-white py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-[32px] sm:text-[44px] font-extrabold tracking-tight leading-[1.1]">
            Your Resume Is Not the Problem.
            <br />
            <span className="text-[#C9A84C]">The Game Changed.</span>
          </h2>
          <div className="mt-8 space-y-5 text-[16px] sm:text-[18px] leading-[1.65] text-slate-200">
            <p>
              In 2024 using AI to tailor your resume gave you an edge. In 2026
              everyone is using it. ChatGPT, Rezi, Teal, Gemini, Kickresume. All
              trained on the same data. All producing resumes that cluster
              together statistically when two hundred candidates run the same
              job description through the same tool.
            </p>
            <p>
              Modern ATS platforms trained on machine learning now recognize the
              cluster. They surface the outliers, the candidates whose resumes
              do not look like the AI-generated pile, for human review.
            </p>
            <p className="font-semibold text-white">
              The candidate who used generic AI to look like everyone else is
              now being filtered out by the very system they thought they were
              gaming.
            </p>
          </div>
          <blockquote className="mt-10 pt-8 border-t border-white/15">
            <p className="text-[22px] sm:text-[28px] italic font-light leading-[1.4] text-[#C9A84C]">
              "Looking like everyone else is the new way to get filtered out."
            </p>
          </blockquote>
        </div>
      </section>

      {/* ============ 3 REASONS ============ */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[32px] sm:text-[44px] font-extrabold tracking-tight leading-[1.1] text-slate-900">
              Why TailorWiz Beats Every Other
              <br />
              AI Resume Tool
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ReasonCard
              icon={<Brain className="w-6 h-6" />}
              title="Built on Marcus Hall's Brain"
              body="Trained on 14+ years of executive resume methodology, frameworks, and writing style. Every output sounds like it came from a world renowned executive resume writer. Because it was built by one."
            />
            <ReasonCard
              icon={<ShieldCheck className="w-6 h-6" />}
              title="Built for the 2026 ATS"
              body="Optimized for how today's algorithms actually evaluate resumes. Semantic relevance. Modern parsing. Section weighting. Everything ChatGPT does not know."
            />
            <ReasonCard
              icon={<Search className="w-6 h-6" />}
              title="Powered by JobIntel 360"
              body="Searches 6,000+ verified sources per company. Tailors your resume to the real problems the team is under pressure to solve. Not just the job description."
            />
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS PREVIEW ============ */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-[32px] sm:text-[44px] font-extrabold tracking-tight leading-[1.1] text-slate-900">
              From Job Description to Hiring Manager's Inbox
              <br />
              <span className="text-[#C9A84C]">in 5 Steps</span>
            </h2>
          </div>
          <ol className="space-y-3">
            {[
              {
                step: "01",
                kicker: "QUALIFY",
                body: "Paste the job description. TailorWiz tells you in seconds whether you are qualified, where the gaps are, and whether you should go for it.",
              },
              {
                step: "02",
                kicker: "TEST",
                body: "Run your current resume through the 2026 ATS algorithm test and the hiring manager eye test. Find out if it passes before you apply.",
              },
              {
                step: "03",
                kicker: "TAILOR",
                body: "Generate your full document package in under 60 seconds. Resume, cover letter, Solution Asset, LinkedIn summary, and more. 90% match every time.",
              },
              {
                step: "04",
                kicker: "REVIEW",
                body: "See the complete before and after report. Every change explained. Every gap closed. Nothing in your document is a mystery.",
              },
              {
                step: "05",
                kicker: "DELIVER",
                body: "Send directly to the hiring manager. Bypass the ATS. Bypass the recruiter. Bypass HR. Land in the decision maker's inbox.",
              },
            ].map((s) => (
              <li
                key={s.step}
                className="flex gap-4 sm:gap-6 items-start p-5 sm:p-6 bg-white rounded-xl border border-slate-200 hover:border-[#C9A84C]/50 hover:shadow-md transition-all"
              >
                <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#0B1C3F] text-white flex items-center justify-center font-extrabold text-[20px] sm:text-[22px]">
                  {s.step}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-[11px] sm:text-[12px] font-bold tracking-[0.15em] uppercase text-[#C9A84C]">
                    {s.kicker}
                  </span>
                  <p className="mt-1 text-[15px] sm:text-[17px] leading-[1.55] text-slate-700">
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10 text-center">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#0B1C3F] text-[#0B1C3F] font-bold hover:bg-[#0B1C3F] hover:text-white transition-colors"
            >
              See the full 5-step workflow <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ SOLUTION ASSET HIGHLIGHT ============ */}
      <section className="py-20 sm:py-24 bg-gradient-to-br from-[#0B1C3F] via-[#0B1C3F] to-[#1a2d56] text-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-[#C9A84C]">
                The Breakout Tool
              </span>
              <h2 className="mt-3 text-[32px] sm:text-[44px] font-extrabold tracking-tight leading-[1.1]">
                Stop Applying Like a Candidate.
                <br />
                <span className="text-[#C9A84C]">
                  Start Showing Up Like a Consultant.
                </span>
              </h2>
              <p className="mt-6 text-[16px] sm:text-[18px] leading-[1.65] text-slate-200">
                The Solution Asset is a one-page executive positioning document
                that replaces the traditional cover letter for high-stakes
                applications. It does not ask for the job. It shows the hiring
                manager you have already solved their most pressing verified
                problem with proof to back it up.
              </p>
              <div className="mt-6">
                <Link
                  to="/tools"
                  className="inline-flex items-center gap-2 text-[15px] font-bold text-[#C9A84C] hover:text-white transition-colors"
                >
                  Learn how the Solution Asset works
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <DarkStat value="85%" label="Interview rate" />
              <DarkStat value="1 page" label="Executive positioning" />
              <DarkStat value="All Levels" label="Entry-level to executive" />
              <DarkStat value="60 sec" label="To generate" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ AUTHORITY ============ */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-[28px] sm:text-[40px] font-extrabold tracking-tight leading-[1.15] text-slate-900">
            Built by the World's #1
            <br />
            <span className="text-[#C9A84C]">
              Faith-Based LinkedIn Career Creator
            </span>
          </h2>
          <p className="mt-6 text-[16px] sm:text-[18px] leading-[1.65] text-slate-700 max-w-3xl mx-auto">
            Marcus Hall has spent 14+ years personally writing resumes and
            coaching job seekers — from first roles to 7-figure executives — into the highest paying roles of
            their careers, often in record time. His career strategy content
            reaches 2 million people every month across LinkedIn alone.
          </p>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <AuthorityStat value="75K+" label="engaged followers" />
            <AuthorityStat value="2M+" label="monthly reach" />
            <AuthorityStat value="10K+" label="subscribers in 30 hours of launch" />
            <AuthorityStat value="100K+" label="average views per post" />
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-[34px] sm:text-[48px] font-extrabold tracking-tight leading-[1.05] text-slate-900">
            One Hour Inside TailorWiz Is Worth
            <br />
            <span className="text-[#C9A84C]">Months of Your Old Job Search.</span>
          </h2>
          <p className="mt-6 text-[16px] sm:text-[18px] leading-[1.65] text-slate-700 max-w-2xl mx-auto">
            Five minutes inside TailorWiz Advanced Tailoring is the highest-ROI
            hour of your entire job search. Whether you're going for your{" "}
            <span className="font-semibold text-slate-900">
              first big role or your next six figures
            </span>
            , landing even one month sooner is worth thousands of dollars — and
            months of stress you and your family never have to feel.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-white text-[17px] font-bold tracking-tight shadow-lg shadow-[#C9A84C]/30 hover:bg-[#B89540] hover:shadow-xl transition-all"
            >
              Try It Out FREE Now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/auth"
              className="text-[15px] font-semibold text-[#0B1C3F] hover:text-[#C9A84C] transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
          <p className="mt-4 text-[12px] text-slate-500 font-medium">
            No credit card required.{" "}
            <span className="mx-2 text-slate-300">•</span>
            Cancel anytime.{" "}
            <span className="mx-2 text-slate-300">•</span>
            90% match score guaranteed every run.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */

/** Circular score gauge rendered as a crisp SVG ring (no CSS transforms). */
function ScoreRing({
  percent,
  color,
  trackColor,
}: {
  percent: number;
  color: string;
  trackColor: string;
}) {
  const size = 120; // 1:1 px — viewBox matches width/height, no scaling blur
  const stroke = 11;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - percent / 100);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          /* SVG-native rotation — avoids the CSS-transform rasterization blur */
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[30px] font-extrabold text-[#0B1C3F] leading-none">
          {percent}%
        </span>
        <span className="text-[10px] text-slate-500 font-medium mt-1">Match Rate</span>
      </div>
    </div>
  );
}

/**
 * HeroScoreCard — a TailorWiz before/after Resume Match Analysis card,
 * themed in the navy + gold brand. Sits as the hero's product-proof visual.
 */
function HeroScoreCard() {
  return (
    <div className="relative">
      {/* Gold ambient glow */}
      <div className="absolute -inset-3 bg-gradient-to-tr from-[#C9A84C]/25 via-transparent to-[#4A78C2]/20 rounded-3xl blur-2xl pointer-events-none" />

      <div className="relative bg-white rounded-2xl border border-[#C9A84C]/40 shadow-2xl p-5 sm:p-6 text-left">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0B1C3F] to-[#1a2d56] flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#C9A84C]" />
          </div>
          <h3 className="text-[19px] sm:text-[22px] font-extrabold text-[#0B1C3F] tracking-tight">
            Resume Match Analysis
          </h3>
        </div>

        {/* Position / company row */}
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
          <div className="flex gap-6 min-w-0">
            <div className="min-w-0">
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold">
                Position
              </p>
              <p className="text-[13px] font-bold text-[#0B1C3F] truncate">
                Chief People &amp; Culture Officer
              </p>
            </div>
            <div className="min-w-0 hidden sm:block">
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold">
                Company
              </p>
              <p className="text-[13px] font-bold text-[#0B1C3F] truncate">
                Robin Hood Foundation
              </p>
            </div>
          </div>
          <span className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 text-white text-[11px] font-bold px-3 py-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Successfully Optimized!
          </span>
        </div>

        {/* Before / After / Boost */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          {/* BEFORE */}
          <div className="relative rounded-xl border-2 border-rose-300 pt-8 pb-3 px-2 flex flex-col items-center">
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-rose-600 text-white text-[17px] sm:text-[19px] font-extrabold tracking-[0.12em] px-5 py-1.5 rounded-lg shadow-md shadow-rose-600/30">
              BEFORE
            </span>
            <p className="text-[10px] font-bold uppercase tracking-wide text-rose-600">
              Original Resume
            </p>
            <div className="mt-2">
              <ScoreRing percent={62} color="#E11D48" trackColor="#FCE7EC" />
            </div>
            <span className="mt-2 text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-1 rounded">
              Needs Improvement
            </span>
          </div>

          {/* AFTER */}
          <div className="relative rounded-xl border-2 border-emerald-300 pt-8 pb-3 px-2 flex flex-col items-center">
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[17px] sm:text-[19px] font-extrabold tracking-[0.12em] px-5 py-1.5 rounded-lg shadow-md shadow-emerald-600/30">
              AFTER
            </span>
            <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">
              Tailored Resume
            </p>
            <div className="mt-2">
              <ScoreRing percent={96} color="#059669" trackColor="#D1FAE5" />
            </div>
            <span className="mt-2 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
              Excellent Match!
            </span>
          </div>

          {/* PERFORMANCE BOOST */}
          <div className="rounded-xl border-2 border-[#C9A84C]/50 pt-3 pb-3 px-2 flex flex-col items-center">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#C9A84C] text-center">
              Performance Boost
            </p>
            <div className="mt-2 w-full flex-1 rounded-lg bg-gradient-to-br from-[#0B1C3F] to-[#1a2d56] flex flex-col items-center justify-center py-4 px-2">
              <span className="text-[34px] font-extrabold text-[#C9A84C] leading-none">
                +34
              </span>
              <span className="text-[10px] text-slate-200 font-medium mt-1">
                Points Gained
              </span>
              <div className="mt-2 w-full">
                <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="h-full bg-[#C9A84C] rounded-full"
                    style={{ width: "96%" }}
                  />
                </div>
                <p className="text-[9px] text-slate-300 mt-1 text-center">
                  Progress: 96%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Outcome callout */}
        <div className="mt-4 rounded-xl bg-[#0B1C3F]/[0.04] border border-[#0B1C3F]/10 p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C9A84C]" />
            <h4 className="text-[14px] font-bold text-[#0B1C3F]">
              Outstanding Optimization Achieved!
            </h4>
          </div>
          <p className="mt-1.5 text-[12px] leading-[1.5] text-slate-600">
            Your resume was strategically enhanced, lifting match compatibility from{" "}
            <span className="font-bold text-[#0B1C3F]">62% to 96%</span> — a{" "}
            <span className="font-bold text-[#0B1C3F]">+34 point improvement</span> that
            significantly strengthens your candidacy for the Chief People &amp; Culture
            Officer role at Robin Hood Foundation.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              "ATS Optimized",
              "Keyword Enhanced",
              "Performance Boosted",
            ].map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0B1C3F] bg-[#C9A84C]/15 border border-[#C9A84C]/40 px-2.5 py-1 rounded"
              >
                <CheckCircle2 className="w-3 h-3 text-[#C9A84C]" />
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <span className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1C3F] text-white text-[13px] font-bold py-2.5">
            <BarChart3 className="w-4 h-4" /> Analysis Report
          </span>
          <span className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#C9A84C] text-[#0B1C3F] text-[13px] font-bold py-2.5">
            <CheckCircle2 className="w-4 h-4" /> Resume Library
          </span>
        </div>
      </div>

      {/* Caption */}
      <p className="mt-3 text-center text-[12px] text-slate-300">
        A real TailorWiz run — generic resume to{" "}
        <span className="text-[#C9A84C] font-semibold">96% match</span> in under 60 seconds.
      </p>
    </div>
  );
}

export function MarketingNav() {
  return (
    <>
    {/* Expert-help banner — visible on every marketing page */}
    <Link
      to="/free-evaluation"
      className="block bg-[#C9A84C] hover:bg-[#E5C36A] text-[#0B1C3F] text-center px-4 py-2.5 text-[13px] sm:text-[14px] font-bold transition-colors"
    >
      🎯 Get a <span className="underline underline-offset-2">FREE Resume Evaluation</span> from one of our experts
      <span className="hidden sm:inline"> — or want it all done FOR you? Book your free 1-on-1 strategy call</span> →
    </Link>
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-lg bg-[#0B1C3F] border border-[#C9A84C]/40 flex items-center justify-center">
            <Wand2 className="w-[18px] h-[18px] text-[#C9A84C]" />
          </span>
          <span className="font-extrabold text-[18px] tracking-tight text-[#0B1C3F]">
            Tailor<span className="text-[#C9A84C]">Wiz</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-[14px] font-medium text-slate-700">
          <Link to="/" className="hover:text-[#C9A84C]">Home</Link>
          <Link to="/tools" className="hover:text-[#C9A84C]">Tools</Link>
          <Link to="/how-it-works" className="hover:text-[#C9A84C]">How It Works</Link>
          <Link to="/pricing" className="hover:text-[#C9A84C]">Pricing</Link>
          <Link to="/benefits" className="hover:text-[#C9A84C]">Benefits</Link>
          <Link to="/contact" className="hover:text-[#C9A84C]">Contact</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/auth"
            className="hidden sm:inline-block text-[14px] font-semibold text-slate-700 hover:text-[#0B1C3F] px-3 py-2"
          >
            Sign in
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-full bg-[#0B1C3F] text-white text-[14px] font-bold shadow border border-[#C9A84C] hover:bg-[#0E2350] transition-colors"
          >
            Try It FREE <ArrowRight className="w-3.5 h-3.5 text-[#C9A84C]" />
          </Link>
        </div>
      </div>
    </header>
    </>
  );
}

export function MarketingFooter() {
  return (
    <footer className="bg-[#0B1C3F] text-slate-300 py-14">
      <div className="max-w-6xl mx-auto px-6 text-center">
        <p className="text-[15px] sm:text-[17px] italic text-[#C9A84C] font-light">
          Tailored to the company. Not the posting.
        </p>
        <nav className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[13px] font-medium">
          {[
            { to: "/", label: "Home" },
            { to: "/tools", label: "Tools" },
            { to: "/how-it-works", label: "How It Works" },
            { to: "/pricing", label: "Pricing" },
            { to: "/benefits", label: "Benefits" },
            { to: "/auth", label: "Login" },
            { to: "/contact", label: "Contact" },
            { to: "/privacy-policy", label: "Privacy" },
            { to: "/terms-of-service", label: "Terms" },
          ].map((l) => (
            <Link
              key={l.to + l.label}
              to={l.to}
              className="hover:text-[#C9A84C] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <p className="mt-8 text-[12px] text-slate-400">
          © 2026 TailorWiz. A product of Results Driven Resumes. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-white p-5 sm:p-6 text-center">
      <div className="text-[20px] sm:text-[26px] font-extrabold text-[#0B1C3F] tracking-tight">
        {value}
      </div>
      <div className="mt-1 text-[12px] sm:text-[13px] text-slate-600 leading-snug">
        {label}
      </div>
    </div>
  );
}

function DarkHeroStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-[#0B1C3F]/30 p-5 sm:p-6 text-center">
      <div className="text-[20px] sm:text-[26px] font-extrabold text-[#C9A84C] tracking-tight">
        {value}
      </div>
      <div className="mt-1 text-[12px] sm:text-[13px] text-slate-200 leading-snug">
        {label}
      </div>
    </div>
  );
}

function DarkStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
      <div className="text-[22px] sm:text-[28px] font-extrabold text-[#C9A84C] tracking-tight">
        {value}
      </div>
      <div className="mt-1 text-[12px] sm:text-[13px] text-slate-300">{label}</div>
    </div>
  );
}

function AuthorityStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-slate-50 rounded-xl p-5 text-center border border-slate-100">
      <div className="text-[24px] sm:text-[32px] font-extrabold text-[#0B1C3F] tracking-tight">
        {value}
      </div>
      <div className="mt-1 text-[12px] sm:text-[13px] text-slate-600 font-medium">
        {label}
      </div>
    </div>
  );
}

function ReasonCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="group relative p-7 rounded-2xl border border-slate-200 bg-white hover:border-[#C9A84C]/60 hover:shadow-xl transition-all">
      <div className="w-12 h-12 rounded-xl bg-[#0B1C3F] text-[#C9A84C] flex items-center justify-center mb-5 group-hover:bg-[#C9A84C] group-hover:text-white transition-colors">
        {icon}
      </div>
      <h3 className="text-[19px] sm:text-[21px] font-bold text-slate-900 leading-tight">
        {title}
      </h3>
      <p className="mt-3 text-[15px] leading-[1.6] text-slate-600">{body}</p>
    </div>
  );
}

// Keep unused-import safe for tree-shaking
void [Target, FileCheck2, Send];
